import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, TrendingUp, Users, DollarSign, BarChart3, ClipboardCheck, GraduationCap, MonitorSmartphone, Calculator } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import siteConfig from '../config/siteContent';

const ServicesPage = () => {
  const [expandedService, setExpandedService] = useState(null);

  const services = [
    {
      icon: Building2,
      title: 'Hotel Acquisition & Due Diligence',
      description: 'Comprehensive feasibility analysis, due diligence support, financial modelling, risk assessment, and turnaround strategy development.',
      features: [
        'Market analysis and positioning',
        'Financial feasibility studies',
        'Operational assessment',
        'Risk identification and mitigation',
        'Investment structure optimization'
      ]
    },
    {
      icon: BarChart3,
      title: 'Asset & Hotel Management',
      description: 'Full-service hotel management with operational oversight, revenue strategy, brand compliance, and performance optimization.',
      features: [
        'Daily operational management',
        'Revenue management strategy',
        'Staff structure and performance',
        'Quality assurance and compliance',
        'Guest satisfaction optimization'
      ]
    },
    {
      icon: ClipboardCheck,
      title: 'Franchise & Brand Expertise',
      description: 'Extensive experience operating within major franchise systems, ensuring brand compliance while maximizing profitability.',
      features: [
        'Brand audit compliance',
        'System implementation',
        'KPI alignment and reporting',
        'Guest satisfaction management',
        'Owner reporting and transparency'
      ]
    },
    {
      icon: Users,
      title: 'Corporate Sales & Business Development',
      description: 'Strategic corporate account acquisition and business development to drive sustainable revenue growth.',
      features: [
        'Corporate account acquisition',
        'Local area market penetration',
        'Government contract development',
        'Long-stay and relocation programs',
        'Strategic partnership development'
      ]
    },
    {
      icon: DollarSign,
      title: 'Financial Performance Management',
      description: 'Disciplined financial management with comprehensive budgeting, forecasting, and performance optimization.',
      features: [
        'Budget preparation and management',
        'Financial forecasting and reforecasting',
        'EBITDA optimization',
        'Capital expenditure planning',
        'Cash flow management'
      ]
    },
    {
      icon: TrendingUp,
      title: 'Turnaround & Repositioning',
      description: 'Specialized expertise in identifying and executing strategic turnarounds for underperforming assets.',
      features: [
        'Performance analysis and diagnosis',
        'Strategic repositioning plans',
        'Operational restructuring',
        'Revenue enhancement strategies',
        'Cost optimization programs'
      ]
    }
  ];

  // Additional services from config
  const additionalServices = [
    {
      icon: GraduationCap,
      ...siteConfig.additionalServices[0]
    },
    {
      icon: MonitorSmartphone,
      ...siteConfig.additionalServices[1]
    },
    {
      icon: Calculator,
      ...siteConfig.additionalServices[2]
    }
  ];

  const approach = [
    {
      step: '01',
      title: 'Discovery & Assessment',
      description: 'Comprehensive analysis of your property, market position, financial performance, and growth opportunities.'
    },
    {
      step: '02',
      title: 'Strategic Planning',
      description: 'Development of customized strategies aligned with your investment goals and market opportunities.'
    },
    {
      step: '03',
      title: 'Implementation',
      description: 'Hands-on execution with active leadership involvement and regular progress monitoring.'
    },
    {
      step: '04',
      title: 'Optimization & Growth',
      description: 'Continuous improvement through data-driven decisions and performance optimization.'
    }
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative h-[70vh] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/32978233/pexels-photo-32978233.jpeg)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
        </div>
        
        <div className="relative h-full flex flex-col items-center justify-center text-white px-8 text-center pt-24">
          <h1 className="font-playfair text-5xl md:text-7xl font-bold mb-6 tracking-wide">
            Our Services
          </h1>
          <p className="text-xl md:text-2xl font-light max-w-3xl">
            Comprehensive hotel management and advisory services designed to maximize asset performance
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-6">
              Core Services
            </h2>
            <p className="text-xl text-[#3A2E24] max-w-3xl mx-auto">
              End-to-end hospitality solutions backed by decades of operational experience and proven results.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={index} 
                  className="p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-t-[#B89C4A]"
                >
                  <div className="w-16 h-16 bg-[#B89C4A] rounded-lg flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-playfair text-2xl font-bold text-[#2B2B2B] mb-4">
                    {service.title}
                  </h3>
                  <p className="text-[#3A2E24] leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-3">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-[#B89C4A] rounded-full mt-2 flex-shrink-0" />
                        <span className="text-sm text-[#3A2E24]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Services - Expanded Cards */}
      <section className="py-32 px-8 bg-[#F4F4F4]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-6">
              Specialized Services
            </h2>
            <p className="text-xl text-[#3A2E24] max-w-3xl mx-auto">
              Comprehensive support services to elevate your hotel operations and maximize performance.
            </p>
          </div>

          <div className="space-y-8">
            {additionalServices.map((service, index) => {
              const Icon = service.icon;
              const isExpanded = expandedService === index;
              
              return (
                <Card 
                  key={index} 
                  className="overflow-hidden hover:shadow-2xl transition-all duration-300"
                >
                  <div 
                    className="p-8 cursor-pointer"
                    onClick={() => setExpandedService(isExpanded ? null : index)}
                  >
                    <div className="flex items-start gap-6 mb-6">
                      <div className="w-20 h-20 bg-[#B89C4A] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-10 h-10 text-white" />
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-playfair text-3xl font-bold text-[#2B2B2B]">
                            {service.title}
                          </h3>
                          <button className="text-[#B89C4A] hover:text-[#A58B3A] transition-colors">
                            {isExpanded ? (
                              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                            ) : (
                              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            )}
                          </button>
                        </div>
                        <p className="text-xl text-[#3A2E24] leading-relaxed mb-4">
                          {service.description}
                        </p>
                        {!isExpanded && (
                          <p className="text-[#B89C4A] font-semibold">Click to learn more →</p>
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-8 pt-8 border-t border-gray-200 animate-fade-in">
                        <p className="text-lg text-[#3A2E24] leading-relaxed mb-8">
                          {service.fullDescription}
                        </p>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                          <div>
                            <h4 className="font-playfair text-xl font-bold text-[#2B2B2B] mb-4 flex items-center gap-2">
                              <ClipboardCheck className="w-5 h-5 text-[#B89C4A]" />
                              What's Included
                            </h4>
                            <ul className="space-y-3">
                              {service.features.map((feature, idx) => (
                                <li key={idx} className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-[#B89C4A] rounded-full mt-2 flex-shrink-0" />
                                  <span className="text-[#3A2E24]">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div>
                            <h4 className="font-playfair text-xl font-bold text-[#2B2B2B] mb-4 flex items-center gap-2">
                              <TrendingUp className="w-5 h-5 text-[#B89C4A]" />
                              Key Benefits
                            </h4>
                            <ul className="space-y-3">
                              {service.benefits.map((benefit, idx) => (
                                <li key={idx} className="flex items-start gap-3">
                                  <div className="w-6 h-6 bg-[#B89C4A] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                  <span className="text-[#3A2E24] font-medium">{benefit}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        <div className="bg-[#B89C4A]/10 p-6 rounded-lg">
                          <p className="text-[#2B2B2B] font-semibold mb-2">Interested in this service?</p>
                          <p className="text-[#3A2E24] mb-4">Contact us to discuss how we can customize this service to meet your specific needs.</p>
                          <Button 
                            size="sm" 
                            className="bg-[#B89C4A] hover:bg-[#A58B3A] text-white"
                            asChild
                          >
                            <Link to="/contact">Get in Touch <ArrowRight className="ml-2 w-4 h-4" /></Link>
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-6">
              Our Approach
            </h2>
            <p className="text-xl text-[#3A2E24] max-w-3xl mx-auto">
              A proven methodology that combines strategic thinking with hands-on execution.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {approach.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white p-8 rounded-lg shadow-lg h-full hover:shadow-2xl transition-shadow duration-300">
                  <div className="text-6xl font-bold text-[#B89C4A]/20 font-playfair mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-playfair text-xl font-bold text-[#2B2B2B] mb-4">
                    {item.title}
                  </h3>
                  <p className="text-[#3A2E24] leading-relaxed">
                    {item.description}
                  </p>
                </div>
                {index < approach.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-[#B89C4A]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-32 px-8 bg-[#F4F4F4]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-8">
                Why Partner With Us
              </h2>
              <div className="space-y-6">
                {[
                  'Proven asset turnaround success',
                  'Franchise award nominations and recognition',
                  '$30M corporate account management experience',
                  'Deep operational knowledge—not just theory',
                  'Long-term investment mindset',
                  'Owner-operator discipline and transparency',
                  'Hands-on leadership approach',
                  'Data-driven decision making'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-[#B89C4A] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-lg text-[#3A2E24]">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative h-[600px] rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1668480441891-3744c25337a3" 
                alt="Hotel Management"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-8 bg-[#2B2B2B] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold mb-8">
            Ready to Elevate Your Hotel's Performance?
          </h2>
          <p className="text-xl text-gray-300 leading-relaxed mb-12">
            Let's discuss how our services can help you achieve your investment goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button 
              size="lg" 
              className="bg-[#B89C4A] hover:bg-[#A58B3A] text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
              asChild
            >
              <Link to="/contact">Schedule a Consultation <ArrowRight className="ml-2" /></Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-[#2B2B2B] px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
              asChild
            >
              <Link to="/case-studies">View Case Studies</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
